import { forwardRef, ReactNode, useImperativeHandle } from 'react';
import { useDropzone } from 'react-dropzone';
import UploadFile from '@mui/icons-material/UploadFile';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Row } from '../Layout';
import { FileData, SharedProps } from './types';
import { getExtensionByType } from '../../utils/files';

const Wrapper = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'isDragActive' &&
    prop !== 'isDragReject' &&
    prop !== 'isDragAccept',
})<{ isDragActive?: boolean; isDragReject?: boolean; isDragAccept?: boolean }>(
  ({ theme, isDragActive, isDragReject, isDragAccept }) => {
    let borderColor = theme.palette.borderColor.main;

    if (isDragReject) {
      borderColor = theme.palette.error.main;
    } else if (isDragAccept) {
      borderColor = theme.palette.success.main;
    } else if (isDragActive) {
      borderColor = theme.palette.success.main;
    }

    return {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderStyle: 'dashed',
      borderWidth: '1px',
      borderColor,
      borderRadius: theme.shape.borderRadius,
      padding: theme.spacing(3, 3),
      cursor: 'pointer',
    };
  },
);

export type DropzoneHandle = { open: () => void };

export type DropzoneProps = Pick<
  SharedProps,
  'sizeRestriction' | 'supportedFormats'
> & {
  maxFiles?: number;
  name?: string;
  height?: number;
  disabled?: boolean;
  content?: ReactNode;
  onDropFile?: (
    droppedFiles: Array<Omit<Required<FileData>, 'isUploading'>>,
  ) => void;
};

const Dropzone = forwardRef<DropzoneHandle, DropzoneProps>(
  (
    {
      supportedFormats = [],
      maxFiles,
      sizeRestriction,
      disabled,
      name,
      height,
      content,
      onDropFile,
    },
    ref,
  ) => {
    const {
      getRootProps,
      getInputProps,
      isDragActive,
      isDragReject,
      isDragAccept,
      open,
    } = useDropzone({
      accept: supportedFormats.reduce(
        (acc, format) => ({
          ...acc,
          [format]: [`.${getExtensionByType(format)}`],
        }),
        {},
      ),
      disabled,
      maxFiles,
      ...(sizeRestriction && {
        maxSize:
          sizeRestriction.value *
          1024 *
          (sizeRestriction.unit === 'kb' ? 1 : 1024),
      }),
      onDrop: (files) => {
        if (!onDropFile) return;
        onDropFile(
          files.map((file) => ({
            previewUri: URL.createObjectURL(file),
            fileName: file.name,
            fileData: file,
          })),
        );
      },
    });

    useImperativeHandle(ref, () => ({ open }));

    return (
      <Wrapper
        height={height}
        isDragActive={isDragActive}
        isDragReject={isDragReject}
        isDragAccept={isDragAccept}
        {...getRootProps()}
      >
        <input {...getInputProps()} name={name} />
        {content ?? (
          <Row centered justify="center" spacing="xs">
            <UploadFile />
            <Typography variant="body2">
              Drag your file(s) here, or{' '}
              <Typography color="info.main" variant="h6" component="span">
                click to upload
              </Typography>
            </Typography>
          </Row>
        )}
      </Wrapper>
    );
  },
);

export default Dropzone;
